import { autoinject } from 'aurelia-dependency-injection';
import { customElement } from 'aurelia-templating';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MdcMenuSurface } from '@aurelia-mdc-web/menu-surface';
import { InstrumentTypeViewModel, QueryInstrumentsMode, QueryTransactionInstrumentsRequest, InstrumentsClient as TransactionInstrumentsClient, VoucherState } from '@dynamik/common/clients/transaction-clients';
import { CustomerSettings } from '../../classes/customer-settings';
import { AvailableInstrumentClient as LookupAvailableInstrumentClient, QueryLookupInstrumentsRequest } from '@dynamik/common/clients/instrument-clients';
import { debounceTime, Subject, switchMap, takeUntil, tap } from '../../rxjs-local';
import { MdcList } from '@aurelia-mdc-web/list';
import { closest } from '@material/dom/ponyfill';
import { bindingMode } from 'aurelia-binding';
import { IValidatedElement, IError } from '@aurelia-mdc-web/base';
import { InstrumentType } from '@dynamik/common/clients/policy-clients';
import { MdcDialogService } from '@aurelia-mdc-web/dialog';
import { EditInstrumentDialog, IEditInstrumentDialogModel } from '../dialogs/edit-instrument-dialog/edit-instrument-dialog';
import { AlertService } from '@aurelia-toolkit/alert';
import { IsinTaxableConfigurationsCache } from '../../services/isin-taxable-configurations-cache';
import { CombinedQueryInstrumentsResponseItem, InstrumentTypesCache } from '@dynamik/common';

const inputEvents = ['click', 'input', 'keydown', 'blur'];
const bodyEvents = ['pointerdown'];

interface IInstrumentsGroup {
  type: string;
  instruments: CombinedQueryInstrumentsResponseItem[];
  expanded?: boolean;
}

@autoinject
@customElement('instrument-lookup')
export class InstrumentLookup implements EventListenerObject {
  constructor(public root: Element, private instrumentTypesCache: InstrumentTypesCache, public alertService: AlertService,
    private customerSettings: CustomerSettings, private transactionInstrumentsClient: TransactionInstrumentsClient,
    private lookupAvailableInstrumentClient: LookupAvailableInstrumentClient, private dialogService: MdcDialogService,
    private itcCache: IsinTaxableConfigurationsCache) {
    defineInstrumentLookupElementApis(this.root as HTMLElement);
  }

  input: HTMLInputElement & { isFocused: boolean };
  menuSurface: MdcMenuSurface;
  groups?: IInstrumentsGroup[];
  searching: boolean = false;
  errorMessage: string | undefined = undefined;
  instrumentTypes: InstrumentTypeViewModel[];
  customerInstrumentTypes: InstrumentType[];
  filterChanged$ = new Subject<string>();
  destroy$ = new Subject();
  list: MdcList;
  initialised = false;

  @bindable({ defaultBindingMode: bindingMode.twoWay, coerce: 'none' })
  value: CombinedQueryInstrumentsResponseItem;
  suppressValueChanged: boolean;
  async valueChanged() {
    if (this.suppressValueChanged) {
      this.suppressValueChanged = false;
      return;
    }
    await this.updateFilterBasedOnValue();
    this.root.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { value: this.value } }));
  }
  suppressChangeAndSetValue(value: CombinedQueryInstrumentsResponseItem) {
    if (this.value === value) {
      return;
    }
    this.suppressValueChanged = true;
    this.value = value;
  }

  @bindable.none
  mode: QueryInstrumentsMode = QueryInstrumentsMode.All;
  modeChanged() {
    if (!this.value) {
      this.filterChanged();
    }
  }

  @bindable
  placeholder: string;

  @bindable.none
  templateVoucher: VoucherState;

  @bindable.booleanAttr
  hideEdit: boolean;

  @bindable.booleanAttr
  disabled: boolean;

  bind() {
    // suppress changed handlers
  }

  async attached() {
    [this.instrumentTypes, this.customerInstrumentTypes] = await Promise.all([
      this.instrumentTypesCache.instrumentTypes,
      this.instrumentTypesCache.customerInstrumentTypes
    ]);
    if (this.input) {
      inputEvents.forEach(x => this.input.addEventListener(x, this));
    }
    await this.updateFilterBasedOnValue();
    bodyEvents.forEach(x => document.body.addEventListener(x, this));
    this.initialised = true;
  }

  detached() {
    if (this.input) {
      inputEvents.forEach(x => this.input.removeEventListener(x, this));
    }
    bodyEvents.forEach(x => document.body.removeEventListener(x, this));
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  handleEvent(evt: Event): void {
    switch (evt.currentTarget) {
      case this.input:
        switch (evt.type) {
          case 'click': this.open(); break;
          case 'input': this.filterChanged(); break;
          case 'keydown': this.onInputKeydown(evt as KeyboardEvent); break;
          case 'blur': this.onBlur(); break;
        }
        break;
      case document.body:
        switch (evt.type) {
          case 'pointerdown': this.onBodyPointerDown(evt as MouseEvent); break;
        }
        break;
    }
  }

  setFilter(filter: string | undefined) {
    if (!this.input || this.input.value === filter) {
      return;
    }
    this.input.value = filter ?? '';
  }

  async updateFilterBasedOnValue() {
    if (this.value !== undefined) {
      this.groups = await this.getInstruments(undefined, this.value);
    } else {
      this.groups = undefined;
    }
    if (this.groups?.length) {
      this.setFilter(this.groups[0].instruments[0].name);
    } else {
      this.setFilter(undefined);
    }
  }

  open() {
    if (this.input?.disabled || this.input?.readOnly || this.menuSurface.open
      || this.groups === undefined && !this.searching && !this.errorMessage) {
      return;
    }
    this.menuSurface.root.style.width = `${this.input?.clientWidth}px`;
    this.menuSurface.open = true;
  }

  close() {
    this.menuSurface.open = false;
  }

  async getInstruments(filter: string, value: CombinedQueryInstrumentsResponseItem): Promise<IInstrumentsGroup[]> {
    if (value) {
      const res: IInstrumentsGroup = { type: 'Selected', instruments: [value], expanded: true };
      return Promise.resolve([res]);
    }

    if (!filter) {
      return [];
    }

    let [lookupInstruments, transInstruments] = await Promise.all([
      this.lookupAvailableInstrumentClient.searchFuzzy(this.customerSettings.selectedCustomer.companyId, new QueryLookupInstrumentsRequest({
        companyIdentityId: this.customerSettings.selectedCustomer.companyId,
        searchString: filter ?? value.isin
      })),
      this.transactionInstrumentsClient.search(this.customerSettings.selectedCustomer.companyId, new QueryTransactionInstrumentsRequest({
        companyIdentityId: this.customerSettings.selectedCustomer.companyId,
        searchString: filter ?? value.isin,
        mode: QueryInstrumentsMode.All, // need to query all to later remove existing from lookup results
        takeLimit: 40
      }))
    ]);
    await this.itcCache.get();
    lookupInstruments.forEach(x => {
      // duplicate instruments will be removed anyway and what is left is 100% non-existing
      x.existingInstrument = false;
      if (x.instrumentTaxable === undefined) {
        // fetch default taxable based on noted isin prefix
        x.instrumentTaxable = this.itcCache.isTaxable(x.isin);
      }
    });

    // reconstruct some noted instrument details and remove duplicates from lookupInstruments
    transInstruments.filter(x => !!x.lookupAvailableInstrumentId).forEach(x => {
      const li = lookupInstruments.find(y => y.lookupAvailableInstrumentId === x.lookupAvailableInstrumentId);
      if (li) {
        x.typeShortName = li.typeShortName;
        x.mic = li.mic;
        x.sortOrder = li.sortOrder;
        x.instrumentTypeId ??= li.instrumentTypeId as number;
        lookupInstruments.remove(li);
      }
    });

    // combine instruments from both apis removing certain instrument types and existing instruments based on mode
    const allInstruments = [
      ...transInstruments.filter(x => !x.instrumentTypeId || this.customerInstrumentTypes.includes(x.instrumentTypeId as number)),
      ...lookupInstruments.filter(x => this.customerInstrumentTypes.some(cr => x.possibleInstrumentTypesIds.includes(cr as number)))
    ].filter(x => this.mode === QueryInstrumentsMode.All ||
      this.mode === QueryInstrumentsMode.OnlyNew && !x.existingInstrument ||
      this.mode === QueryInstrumentsMode.OnlyExisting && x.existingInstrument
    );

    // group instruments by type from trans api or by typeShortName from lookup api
    // prefixing the group name with 'Unnoted ' will automatically put unnoted at the end of a sorted list
    const groups = allInstruments.groupBy(x => `${x.lookupAvailableInstrumentId ? '' : 'Unnoted '}${x.instrumentTypeId ? this.instrumentTypes.find(y => y.id === x.instrumentTypeId)?.name : (x.typeShortName ?? 'Unknown')}`);
    return Array.from(groups, ([type, instruments]) => ({
      type,
      instruments: instruments.sort((a, b) => {
        if (a.existingInstrument && !b.existingInstrument) {
          return -1;
        }
        if (!a.existingInstrument && b.existingInstrument) {
          return 1;
        }
        return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      })
    })).sort((a, b) => a.type.localeCompare(b.type));
  }

  filterChangedSub$ = this.filterChanged$
    .pipe(
      debounceTime(500),
      tap(() => {
        this.searching = true;
        this.errorMessage = undefined;
        // this.suppressChangeAndSetValue(undefined);
        this.groups = undefined;
        if (this.input?.isFocused === true) {
          this.open();
        }
      }),
      switchMap(async x => this.getInstruments(x, undefined).catch(e => e as Error)),
      takeUntil(this.destroy$),
    )
    .subscribe(x => {
      if (x instanceof Error) {
        this.errorMessage = x.message;
      } else {
        this.groups = x;
      }
      this.searching = false;
    });

  filterChanged() {
    this.filterChanged$.next(this.input?.value);
  }

  onInputKeydown(evt: KeyboardEvent) {
    switch (evt.code) {
      case 'ArrowDown':
        if (!this.menuSurface.open) {
          this.open();
        }
        this.suppressBlur = true;
        this.list?.foundation?.focusFirstElement();
        break;
      case 'ArrowUp':
        if (!this.menuSurface.open) {
          this.open();
        }
        this.suppressBlur = true;
        this.list?.foundation?.focusLastElement();
        break;
      case 'Space':
        if (evt.ctrlKey) {
          if (this.menuSurface.open) {
            this.close();
          } else {
            this.open();
            if (!this.value) {
              this.filterChanged();
            }
          }
        }
        break;
    }
  }

  async select(i: CombinedQueryInstrumentsResponseItem) {
    this.value = i;
    this.close();
    this.input?.focus();
  }

  suppressBlur: boolean;
  async onBlur() {
    if (this.suppressBlur) {
      this.suppressBlur = false;
      return;
    }
    if (this.input?.value.length === 0) {
      this.value = undefined;
    }
    this.close();
    // re-emit on root
    this.root.dispatchEvent(new CustomEvent('blur'));
  }

  onBodyPointerDown(evt: MouseEvent | TouchEvent) {
    if (closest(evt.target as HTMLElement, '.mdc-menu-surface') === this.menuSurface.root) {
      evt.preventDefault();
    }
    return true;
  }

  async add() {
    const model: IEditInstrumentDialogModel = {
      templateVoucher: this.templateVoucher,
      isEditOfExistingInstrument: false
    };
    const reason = await this.dialogService.open({ viewModel: EditInstrumentDialog, model });
    if (reason === 'save') {
      this.value = model.instrument;
    }
  }

  clear() {
    this.value = undefined;
  }

  addError(error: IError) {
    if (this.input && Object.getOwnPropertyDescriptor(this.input, 'addError')) {
      (this.input as HTMLElement as IValidatedElement).addError(error);
    }
  }

  removeError(error: IError) {
    if (this.input && Object.getOwnPropertyDescriptor(this.input, 'addError')) {
      (this.input as HTMLElement as IValidatedElement).removeError(error);
    }
  }

  renderErrors() {
    if (this.input && Object.getOwnPropertyDescriptor(this.input, 'renderErrors')) {
      (this.input as HTMLElement as IValidatedElement).renderErrors();
    }
  }

  async edit() {
    const model: IEditInstrumentDialogModel = {
      transactionAvailableInstrumentId: this.value.transactionAvailableInstrumentId,
      transactionInstrumentId: this.value.transactionInstrumentId,
      lookupAvailableInstrumentId: this.value.lookupAvailableInstrumentId,
      isEditOfExistingInstrument: false
    };
    const reason = await this.dialogService.open({ viewModel: EditInstrumentDialog, model });
    if (reason === 'save') {
      this.value = model.instrument;
    }
  }
}

export interface IInstrumentLookupElement extends IValidatedElement {
  au: {
    controller: {
      viewModel: InstrumentLookup;
    };
  };
}

function defineInstrumentLookupElementApis(element: HTMLElement) {
  Object.defineProperties(element, {
    addError: {
      value(this: IInstrumentLookupElement, error: IError) {
        this.au.controller.viewModel.addError(error);
      },
      configurable: true
    },
    removeError: {
      value(this: IInstrumentLookupElement, error: IError) {
        this.au.controller.viewModel.removeError(error);
      },
      configurable: true
    },
    renderErrors: {
      value(this: IInstrumentLookupElement): void {
        this.au.controller.viewModel.renderErrors();
      },
      configurable: true
    },
    focus: {
      value(this: IInstrumentLookupElement) {
        this.au.controller.viewModel.input.focus();
      },
      configurable: true
    }
  });
}

